import {AfterViewInit, Component, OnDestroy, OnInit} from "@angular/core";
import {Router} from "@angular/router";
import {AiInstructionService} from "../../services/ai-instruction.service";
import {AudioTimestamps} from "../../models/custom-models/audio-timestamps.model";
import * as Hark from "hark";
import {Harker} from "hark";
import {SharedService} from "../../services/shared.service";
import {FunctionalityNames} from "../../models/constants";
import {SideNavService} from "../../components/nav-bar/Services/side-nav.service";
import {environment} from "../../../environments/environment";
import {FeatureVisibilityControllerService} from "../../services/feature-visibility-controller.service";
import {RootRoutePaths} from "../../models/routes-constants";
import {Link, LucideAngularModule} from "lucide-angular";
import {MatDialog} from "@angular/material/dialog";
import {JitsiMeetingExpiryDialogComponent} from "../../components/Shared/jitsi-meeting-expiry-dialog/jitsi-meeting-expiry-dialog.component";
import {EXPIRY_DIALOG_RESPONSE, JITSI_MEETING_EXPIRY_TIMER} from "../../models/jitsi-meeting";


declare var JitsiMeetExternalAPI: any;

@Component({
    selector: "app-jitsi-meeting",
    templateUrl: "./jitsi-meeting.component.html",
    styleUrls: ["./jitsi-meeting.component.scss"],
    imports: [
        LucideAngularModule
    ]
})
export class JitsiMeetingComponent implements OnInit, AfterViewInit, OnDestroy {
    domain: string = environment.jitsiMeetingUrl; // For self-hosted use your domain
    roomName: string;
    room: string;
    options: any;
    api: any;
    user: any;
    isUserSpeaking: boolean = false;
    mediaRecorder: MediaRecorder;
    data: any[] = [];

    recordingStartTime: number;
    recordingEndTime: number;
    userAudioDetector: Harker | null = null;
    isSpeakButtonEnabled = false;
    fourSecondTimeout: ReturnType<typeof setTimeout>;
    joinedUserId: string = "";
    isSpeakerOn: boolean = false;
    stream: MediaStream | null = null;
    isJitsiRecordingEnabled: boolean = false;
    protected readonly Link = Link;

    private tokenExpiryTimeMs: number | null = null;
    private expiryCheckInterval: ReturnType<typeof setInterval> | null = null;

    private warned10Min = false;
    private warned5Min = false;
    private warned1Min = false;
    private isAnotherMeetingScheduledToday: boolean;
    private expiryDialogRef: any = null;


    constructor(
        private router: Router,
        private aiInstructionsService: AiInstructionService,
        private sharedService: SharedService,
        private sideNavService: SideNavService,
        private featureVisibilityControllerService: FeatureVisibilityControllerService,
        private dialog: MatDialog,
    ) {
        // Read query params directly from the URL because this component is rendered
        // directly in app.component.html (not via <router-outlet>), so ActivatedRoute
        // may not have query params populated during early bootstrap.
        const urlParams = new URLSearchParams(window.location.search);
        const meetingName = urlParams.get('meetingName');
        const meetingToken = urlParams.get('token');

        this.isAnotherMeetingScheduledToday = urlParams.get('isAnotherMeetingScheduledToday') === 'true';

        if (meetingName) {
            this.room = meetingName;
            this.roomName = meetingName;
        }
        if (meetingToken) {
            this.room = `${this.room}?jwt=${meetingToken}`;

            this.parseJwt(meetingToken);
        }
        if (!this.room) {
            console.error('JitsiMeetingComponent: No meeting room name found in URL. URL:', window.location.href);
            window.close();
        }
    }

    ngOnInit(): void {
        this.sideNavService.close();
        const firstName = this.sharedService.getLoggedInUserFirstName();
        const lastName = this.sharedService.getLoggedInUserLastName();
        this.user = {
            name: `${firstName} ${lastName}`, // Set your username
        };
        this.isJitsiRecordingEnabled = this.featureVisibilityControllerService.isFunctionalityAllowed(
            FunctionalityNames.JitsiMeetRecordingAndAIAnalysis,
        );

        // if meeting room is scheduled for another meeting then only show timer
        if (this.tokenExpiryTimeMs && this.isAnotherMeetingScheduledToday) {
            this.startExpiryWatcher();
        }

    }

    private startExpiryWatcher(): void {
        this.expiryCheckInterval = setInterval(() => {
            if (!this.tokenExpiryTimeMs) return;

            const now = Date.now();
            const diffMs = this.tokenExpiryTimeMs - now;
            const diffMinutes = Math.floor(diffMs / 60000);

            if (diffMs <= 0) {
                if (this.expiryCheckInterval) {
                    clearInterval(this.expiryCheckInterval);
                    this.expiryCheckInterval = null;
                }
                return;
            }

            if (diffMinutes <= JITSI_MEETING_EXPIRY_TIMER.TEN_MINUTES && !this.warned10Min) {
                this.warned10Min = true;
                this.showExpiryPopup(JITSI_MEETING_EXPIRY_TIMER.TEN_MINUTES);
            }

            if (diffMinutes <= JITSI_MEETING_EXPIRY_TIMER.FIVE_MINUTES && !this.warned5Min) {
                this.warned5Min = true;
                this.showExpiryPopup(JITSI_MEETING_EXPIRY_TIMER.FIVE_MINUTES);
            }

            if (diffMinutes <= JITSI_MEETING_EXPIRY_TIMER.ONE_MINUTE && !this.warned1Min) {
                this.warned1Min = true;
                this.showExpiryPopup(JITSI_MEETING_EXPIRY_TIMER.ONE_MINUTE);
            }

        }, 1000);
    }

    private showExpiryPopup(minutes: number): void {
        // If a popup is already open, don’t open another
        if (this.expiryDialogRef) {
            return;
        }

        this.expiryDialogRef = this.dialog
            .open(JitsiMeetingExpiryDialogComponent, {
                width: '430px',
                data: { minutes },
                disableClose: true,
                autoFocus: false,
            });

        this.expiryDialogRef.afterClosed().subscribe((result: string) => {
            // Clear reference so the next popup can open
            this.expiryDialogRef = null;

            if (result === EXPIRY_DIALOG_RESPONSE.EXIT) {
                this.closeMeetingAndTab();
            }
        });
    }

    private closeMeetingAndTab(): void {
        try {
            // Dispose Jitsi if available
            if (this.api) {
                this.api.dispose();
            }
        } catch (e) {
            console.error('Error disposing Jitsi', e);
        }

        // Clear expiry watcher
        if (this.expiryCheckInterval) {
            clearInterval(this.expiryCheckInterval);
            this.expiryCheckInterval = null;
        }

        // Close the tab
        window.close();
    }

    private parseJwt(token: string): void {
        try {
            const payload = token.split('.')[1];
            const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
            const json = JSON.parse(decoded);

            this.tokenExpiryTimeMs = json.exp * 1000;
        } catch (err) {
            console.error('Failed to parse JWT', err);
        }
    }

    private loadJitsiScript(): Promise<void> {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://meet.jit.si/external_api.js';
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject();
            document.body.appendChild(script);
        });
    }

    ngAfterViewInit(): void {
        this.loadJitsiScript()
            .then(() => {
                this.options = {
                    roomName: this.room,
                    configOverwrite: {
                        prejoinConfig: {
                            enabled: true,
                        },
                    },
                    parentNode: document.querySelector("#jitsi-iframe"),
                    userInfo: {
                        displayName: this.user.name,
                    },
                };

                try {
                    this.api = new JitsiMeetExternalAPI(this.domain, this.options);

                    // Event Listeners
                    this.api.addEventListeners({
                        readyToClose: this.handleClose,
                        participantLeft: this.handleParticipantLeft,
                        participantJoined: this.handleParticipantJoined,
                        videoConferenceJoined: this.handleVideoConferenceJoined,
                        videoConferenceLeft: this.handleVideoConferenceLeft,
                        audioMuteStatusChanged: this.handleMuteStatus,
                        videoMuteStatusChanged: this.handleVideoStatus,
                    });

                    // Get media stream only if needed
                    if (this.isJitsiRecordingEnabled) {
                        this.getMediaStream();
                    }

                } catch (e) {
                    console.error('Failed to initialize Jitsi:', e);
                }

            })
            .catch((err) => {
                console.error('Failed to load Jitsi Meet script:', err);
            });
    }


    getMediaStream() {
        navigator.mediaDevices
            .getUserMedia({
                audio: true,
                video: false,
            })
            .then((stream) => {
                this.stream = stream;
                /**
                 * Media Recorder Initialization and Events
                 * */
                this.mediaRecorder = new MediaRecorder(stream);
                this.mediaRecorder.onstart = () => {
                    this.recordingStartTime = new Date().getTime();
                    this.updateSpeakerStatus();
                };

                this.mediaRecorder.ondataavailable = (event: any) => {
                    this.data.length = 0;
                    this.data.push(event.data);
                };

                this.mediaRecorder.onstop = () => {
                    this.recordingEndTime = new Date().getTime();
                    this.mediaRecorderOnStopEventHandler();
                };
            });
    }

    /**
     * Jitsi Meet Event Handlers/Listeners
     * */
    handleClose = () => {
        // console.log("handleClose");
    };

    handleParticipantLeft = async (participant: any) => {
        // console.log("handleParticipantLeft", participant);
    };

    handleParticipantJoined = async (participant: any) => {
        // console.log("handleParticipantJoined", participant);
    };

    handleVideoConferenceJoined = async (participant: any) => {
        if (participant.id) {
            this.joinedUserId = participant.id;
        }
        /**
         * Hark Library Initialization and its Events
         * */
        if (this.stream) {
            this.userAudioDetector = Hark(this.stream);
            this.userAudioDetector?.on("speaking", () => {
                if (this.joinedUserId != "") {
                    this.isUserSpeaking = true;
                    if (this.isSpeakButtonEnabled && this.mediaRecorder && this.mediaRecorder.state != "recording") {
                        this.mediaRecorder.start();
                    }
                    clearTimeout(this.fourSecondTimeout);
                }
            });

            this.userAudioDetector?.on("stopped_speaking", () => {
                if (this.joinedUserId != "") {
                    this.isUserSpeaking = false;
                    if (this.isSpeakButtonEnabled) {
                        this.fourSecondTimeout = setTimeout(() => {
                            if (
                                !this.isUserSpeaking &&
                                this.mediaRecorder.state == "recording" &&
                                this.isSpeakButtonEnabled
                            ) {
                                this.mediaRecorder.stop();
                            }
                        }, 4000);
                    }
                }
            });
        }
    };

    handleVideoConferenceLeft = () => {
        window.close()
    };

    handleMuteStatus = (audio: any) => {
        if (!audio.muted) {
            this.isSpeakButtonEnabled = true;
        } else {
            this.isSpeakButtonEnabled = false;
            if (this.mediaRecorder && this.mediaRecorder?.state == "recording") {
                this.mediaRecorder.stop();
            }
        }
    };

    handleVideoStatus = async (video: any) => {
        // console.log("handleVideoStatus", video); // { muted: true }
    };

    getParticipants() {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                resolve(this.api.getParticipantsInfo()); // get all participants
            }, 500);
        });
    }

    /** This function is basically is used to get the output devices
     *  and update the status of speaker if output device includes speaker
     *  */
    updateSpeakerStatus() {
        //Detect the audioOutput device.
        this.api.getCurrentDevices().then((device: any) => {
            console.log("Devices", device.audioOutput.label.toLowerCase().includes("speaker"));
            this.isSpeakerOn = device.audioOutput.label.toLowerCase().includes("speaker");
        });
    }

    /** This method is used to handle the Media Recorder when user click on mute button.
     * When user click on mute/Unmute button we use this method when state is audio:muted == true
     * */
    mediaRecorderOnStopEventHandler() {
        let blobData = new Blob(this.data, {type: "audio/aac"});
        if (this.recordingEndTime - this.recordingStartTime > 500) {
            //Convert the blobData into the file
            let file = new File([blobData], `${new Date().getTime()}_audio.aac`, {
                lastModified: new Date().getTime(),
                type: blobData.type,
            });
            this.sendAudioToAi(file);
        } else {
            this.data.length = 0;
        }
    }

    /**
     * This method is used to send the audio file to the AI server.
     * */
    sendAudioToAi(file: any) {
        let timeStamps: AudioTimestamps[] = [];
        let timeStamp: AudioTimestamps = {
            start: this.recordingStartTime,
            end: this.recordingEndTime,
        };
        timeStamps.push(timeStamp);

        //Converting file into Multipart/form data before sending to the Ai API
        const audio = new FormData();
        audio.append("file", file);
        audio.append("person_name", this.user.name);
        audio.append("meeting_id", this.room);
        audio.append("speaker_on", String(this.isSpeakerOn));
        audio.append("timestamps", JSON.stringify(timeStamps));
        if (this.data.length > 0) {
            this.aiInstructionsService.transcribeMeeting(audio).subscribe(
                () => {
                    timeStamps.length = 0;
                },
                () => {
                    timeStamps.length = 0;
                },
            );
        }
    }

    ngOnDestroy() {
        if (this.userAudioDetector && this.userAudioDetector.state == "running") {
            this.userAudioDetector.stop();
        }
        this.mediaRecorder?.state == "recording" ? this.mediaRecorder?.stop() : null;
        if (this.stream) {
            // Stopping each track will release the microphone
            this.stream.getTracks()?.forEach((track) => track.stop());
        }
    }

    handleInviteGuest() {
        // lazy load component
        import('./components/jitsi-invite-dialog/jitsi-invite-dialog.component')
            .then(({ JitsiInviteDialogComponent }) => {
                this.dialog
                    .open(JitsiInviteDialogComponent, {
                        width: "500px",
                        height: "480px",
                        position: {
                            bottom: '50px',
                            right: '20px'
                        },
                        data: {
                            roomName: this.roomName,
                        }
                    })
                    .afterClosed()
                    .subscribe((result) => {
                        if (result) {
                            const linkToShare = this.convertTokenToLink(result);
                            import('./components/jitsi-link-dialog/jitsi-link-dialog.component').then(
                                ({ JitsiLinkDialogComponent }) => this.dialog.open(JitsiLinkDialogComponent, {
                                    width: "400px",
                                    height: "180px",
                                    position: {
                                        bottom: '50px',
                                        right: '20px'
                                    },
                                    data: {
                                        inviteLink: linkToShare
                                    }
                                }
                            ));
                        }
                    });
            })
    }

    private convertTokenToLink(result: any) {
        return `${environment.inviteJitsiDomain}${RootRoutePaths.MEET}?meetingName=${this.roomName}&token=${result}`;
    }
}
